/**
 * @description Pack SO shell — light rail, no LynxPay chrome (LYN-360)
 *
 * Key features:
 * - Light sidebar, no top search bar, no Lynx footer
 * - SO branding paints the rail from the existing branding SoT
 * - Ghost rail behind first-run / add-company modal (not live Lynx chrome)
 *
 * @dependencies PackSOSidebar, PackOnboardingHost, AuthContext, CompanyContext
 *
 * @notes Do not import BaseSidebarLayout, AppHeader, or AppFooter.
 *   Untitled npm packages stay out. Brand tokens, not Untitled purple.
 */

import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { usePackChrome } from '../../../contexts/PackChromeContext';
import useRouteTitle from '../../../hooks/useRouteTitle';
import PackOnboardingHost from './PackOnboardingHost';
import PackSOSidebar from './PackSOSidebar';

const SIDEBAR_OPEN_PX = 272;
const SIDEBAR_CLOSED_PX = 72;

const PackSOLayout = ({ outletKey }) => {
  const { companies } = useAuth();
  const { hasCompany } = useCompany();
  const { addCompanyOpen } = usePackChrome();
  useRouteTitle();

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const stored = localStorage.getItem('packSoSidebarOpen');
    return stored !== null ? JSON.parse(stored) : true;
  });
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('packSoSidebarOpen', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  const firstRun = (companies?.length || 0) === 0 && !hasCompany;
  const ghost = firstRun || addCompanyOpen;

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="pack-so-shell">
      {isMobileSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-gray-900/30"
          onClick={() => setIsMobileSidebarOpen(false)}
        >
          <aside
            className="bg-white border-r border-gray-200 w-[272px] max-w-[80vw] h-full"
            onClick={(event) => event.stopPropagation()}
          >
            <PackSOSidebar
              isSidebarOpen
              isMobile
              ghost={ghost}
              onLinkClick={() => setIsMobileSidebarOpen(false)}
            />
          </aside>
        </div>
      )}

      <aside
        className="hidden lg:flex flex-col fixed left-0 top-0 h-screen z-20 bg-white border-r border-gray-200"
        style={{ width: isSidebarOpen ? SIDEBAR_OPEN_PX : SIDEBAR_CLOSED_PX }}
        data-testid="pack-so-desktop-rail"
      >
        <div className="flex-1 min-h-0">
          <PackSOSidebar isSidebarOpen={isSidebarOpen} ghost={ghost} />
        </div>
        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={() => setIsSidebarOpen((open) => !open)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-gray-500 hover:bg-gray-50 hover:text-gray-800"
            aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            disabled={ghost}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isSidebarOpen ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
              />
            </svg>
          </button>
        </div>
      </aside>

      <div className={`flex-1 flex flex-col min-w-0 transition-[margin] duration-200 ${
        isSidebarOpen ? 'lg:ml-[272px]' : 'lg:ml-[72px]'
      }`}>
        <div className="lg:hidden flex items-center h-12 px-3 bg-white border-b border-gray-200">
          <button
            type="button"
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={() => setIsMobileSidebarOpen(true)}
            aria-label="Open menu"
            disabled={ghost}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        <main className="flex-1">
          <PackOnboardingHost />
          <Outlet key={outletKey} />
        </main>
      </div>
    </div>
  );
};

export default PackSOLayout;
